import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tabs, Table, Button, Input, Modal, Descriptions, message, Space } from 'antd';
import { CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { InvoiceManagementPageModel } from '../pageModels/InvoiceManagementPageModel';
import { InvoiceRequest, InvoiceRecord } from '../../models/InvoiceManagement';
import ShowHint from '../../components/ShowHint';
import '../styles/InvoiceManagement.css';
import { globalStore } from '../../models/globalStore';

const { TabPane } = Tabs;

const PAGE_SIZE = 10;

const formatAmount = (amount?: number) => `¥${Number(amount ?? 0).toFixed(2)}`;

const formatInvoiceType = (type?: number | string) =>
    String(type) === "0" ? "普通发票" : "专用发票";

const getInvoiceId = (record: InvoiceRequest | InvoiceRecord) =>
    record.invoiceRequestId || record.id || "";

const getInvoiceNo = (record: InvoiceRequest | InvoiceRecord) => {
    const invoiceRecord = record as InvoiceRecord;
    return invoiceRecord.invoiceNo || invoiceRecord.invoiceNumber || "-";
};

const isPendingStatus = (status?: number | string) =>
    status === 0 || status === "0" || status === "pending";

const InvoiceManagement: React.FC = observer(() => {
    const [model] = useState(() => new InvoiceManagementPageModel());
    const [showHint, setShowHint] = useState(false);
    const [hintMessage, setHintMessage] = useState('');

    useEffect(() => {
        globalStore.selectedMenuKey = 'invoice-management';
        model.loadUnprocessedRequests();
    }, [model]);

    // 待开票记录列定义
    const unprocessedColumns = [
        {
            title: '发票ID',
            key: 'invoiceRequestId',
            width: 120,
            render: (_: unknown, record: any) => getInvoiceId(record),
        },
        {
            title: '买家ID',
            dataIndex: 'buyerId',
            key: 'buyerId',
            width: 120,
        },
        {
            title: '买家名称',
            dataIndex: 'buyerName',
            key: 'buyerName',
            width: 200,
        },
        {
            title: '发票金额',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (amount: number) => formatAmount(amount),
        },
        {
            title: '发票类型',
            dataIndex: 'invoiceType',
            key: 'invoiceType',
            width: 100,
            render: (type: number | string) => formatInvoiceType(type),
        },
        {
            title: '企业抬头',
            dataIndex: 'title',
            key: 'title',
            width: 200,
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_: any, record: any) => (
                <Space>
                    <Button 
                        type="link" 
                        onClick={() => model.showDetailModal(record)}
                        style={{ padding: 0, border: 'none', boxShadow: 'none' }}
                    >
                        查看详情
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={() => model.showProcessModal(getInvoiceId(record))}
                        style={{ borderRadius: 0 }}
                    >
                        开票处理
                    </Button>
                </Space>
            ),
        },
    ];

    // 已开票记录列定义
    const processedColumns = [
        {
            title: '发票ID',
            key: 'invoiceRequestId',
            width: 120,
            render: (_: unknown, record: any) => getInvoiceId(record),
        },
        {
            title: '发票号码',
            key: 'invoiceNo',
            width: 120,
            render: (_: unknown, record: any) => getInvoiceNo(record),
        },
        {
            title: '买家ID',
            dataIndex: 'buyerId',
            key: 'buyerId',
            width: 120,
        },
        {
            title: '买家名称',
            dataIndex: 'buyerName',
            key: 'buyerName',
            width: 200,
        },
        {
            title: '发票金额',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (amount: number) => formatAmount(amount),
        },
        {
            title: '发票类型',
            dataIndex: 'invoiceType',
            key: 'invoiceType',
            width: 100,
            render: (type: number | string) => formatInvoiceType(type),
        },
        {
            title: '企业抬头',
            dataIndex: 'title',
            key: 'title',
            width: 200,
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_: any, record: any) => (
                <Button 
                    type="link" 
                    onClick={() => model.showDetailModal(record)}
                    style={{ padding: 0, border: 'none', boxShadow: 'none' }}
                >
                    查看详情
                </Button>
            ),
        },
    ];

    // 订单列表列定义
    const orderColumns = [
        {
            title: '订单ID',
            dataIndex: 'orderId',
            key: 'orderId',
            width: 120,
            render: (orderId: string) => (
                <Space>
                    <span>{orderId}</span>
                    <CopyOutlined 
                        onClick={async () => {
                            const success = await model.copyToClipboard(orderId);
                            if (success) {
                                setHintMessage('订单ID复制成功');
                                setShowHint(true);
                            }
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    />
                </Space>
            ),
        },
        {
            title: '商品名称',
            dataIndex: 'productName',
            key: 'productName',
            width: 200,
            render: (productName: string) => (
                <Space>
                    <span>{productName}</span>
                    <CopyOutlined 
                        onClick={async () => {
                            const success = await model.copyToClipboard(productName);
                            if (success) {
                                setHintMessage('商品名称复制成功');
                                setShowHint(true);
                            }
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    />
                </Space>
            ),
        },
        {
            title: '卖家ID',
            dataIndex: 'sellerId',
            key: 'sellerId',
            width: 120,
            render: (sellerId: string) => (
                <Space>
                    <span>{sellerId}</span>
                    <CopyOutlined 
                        onClick={async () => {
                            const success = await model.copyToClipboard(sellerId);
                            if (success) {
                                setHintMessage('卖家ID复制成功');
                                setShowHint(true);
                            }
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    />
                </Space>
            ),
        },
        {
            title: '卖家名称',
            dataIndex: 'sellerName',
            key: 'sellerName',
            width: 200,
            render: (sellerName: string) => (
                <Space>
                    <span>{sellerName}</span>
                    <CopyOutlined 
                        onClick={async () => {
                            const success = await model.copyToClipboard(sellerName);
                            if (success) {
                                setHintMessage('卖家名称复制成功');
                                setShowHint(true);
                            }
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    />
                </Space>
            ),
        },
        {
            title: 'SKU类型',
            dataIndex: 'skuType',
            key: 'skuType',
            width: 100,
            render: (skuType: number) => skuType === 0 ? '体验版' : '标准版',
        },
    ];

    const handleTabChange = (activeKey: string) => {
        model.setActiveTab(activeKey);
    };

    const handleSearch = () => {
        model.searchProcessedInvoices();
    };

    const handleProcessInvoice = async () => {
        const success = await model.processInvoice();
        if (success) {
            message.success('开票处理成功');
        } else {
            message.error('开票处理失败');
        }
    };

    return (
        <div className="invoice-management">
            <Tabs 
                activeKey={model.activeTab} 
                onChange={handleTabChange}
                style={{ border: 'none' }}
            >
                <TabPane tab="待开票" key="unprocessed">
                    <div className="tab-content">
                        <Table
                            columns={unprocessedColumns}
                            dataSource={model.unprocessedRequests}
                            rowKey={(record) => getInvoiceId(record)}
                            loading={model.loading}
                            pagination={{
                                pageSize: PAGE_SIZE,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 条`,
                            }}
                            scroll={{ x: 1000 }}
                            size="middle"
                            onRow={(record) => ({
                                onClick: () => model.showDetailModal(record),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </div>
                </TabPane>
                
                <TabPane tab="已开票" key="processed">
                    <div className="tab-content">
                        <div className="search-section">
                            <Input
                                placeholder="请输入买家ID/买家名称/企业抬头/企业信用代码"
                                value={model.searchKeyword}
                                onChange={(e) => model.setSearchKeyword(e.target.value)}
                                style={{ width: 400, marginRight: 16, borderRadius: 0 }}
                                onPressEnter={handleSearch}
                            />
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                style={{ borderRadius: 0 }}
                            >
                                查询
                            </Button>
                        </div>
                        
                        <Table
                            columns={processedColumns}
                            dataSource={model.filteredProcessedInvoices}
                            rowKey={(record) => getInvoiceId(record)}
                            loading={model.loading}
                            pagination={{
                                pageSize: PAGE_SIZE,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 条`,
                            }}
                            scroll={{ x: 1000 }}
                            size="middle"
                            onRow={(record) => ({
                                onClick: () => model.showDetailModal(record),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </div>
                </TabPane>
            </Tabs>

            {/* 详情弹窗 */}
            <Modal
                title="发票详情"
                open={model.detailModalVisible}
                onCancel={model.hideDetailModal}
                footer={null}
                width={800}
                destroyOnClose
            >
                {model.selectedInvoice && (
                    <div>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="发票ID" span={2}>
                                {getInvoiceId(model.selectedInvoice)}
                            </Descriptions.Item>
                            <Descriptions.Item label="买家ID">
                                {model.selectedInvoice.buyerId}
                            </Descriptions.Item>
                            <Descriptions.Item label="买家名称">
                                {model.selectedInvoice.buyerName}
                            </Descriptions.Item>
                            <Descriptions.Item label="发票金额">
                                {formatAmount(model.selectedInvoice.amount)}
                            </Descriptions.Item>
                            <Descriptions.Item label="发票类型">
                                {formatInvoiceType(model.selectedInvoice.invoiceType)}
                            </Descriptions.Item>
                            <Descriptions.Item label="企业抬头">
                                {model.selectedInvoice.title}
                            </Descriptions.Item>
                            <Descriptions.Item label="税号" span={2}>
                                {model.selectedInvoice.taxNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label="开户行">
                                {model.selectedInvoice.bankName || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="账号">
                                {model.selectedInvoice.bankAccount || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="公司地址" span={2}>
                                {model.selectedInvoice.address || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="公司电话">
                                {model.selectedInvoice.phone || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="收票邮箱">
                                {model.selectedInvoice.emailAddress}
                            </Descriptions.Item>
                            {getInvoiceNo(model.selectedInvoice) !== "-" && (
                                <Descriptions.Item label="发票号码" span={2}>
                                    {getInvoiceNo(model.selectedInvoice)}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Space>
                                <Button 
                                    type="link" 
                                    onClick={model.showOrderListModal}
                                    style={{ padding: 0, border: 'none', boxShadow: 'none' }}
                                >
                                    查看订单列表
                                </Button>
                                {isPendingStatus(model.selectedInvoice.status) && (
                                    <Button 
                                        type="primary" 
                                        onClick={model.processInvoiceFromDetail}
                                        style={{ borderRadius: 0 }}
                                    >
                                        开票处理
                                    </Button>
                                )}
                            </Space>
                        </div>
                    </div>
                )}
            </Modal>

            {/* 订单列表弹窗 */}
            <Modal
                title="订单列表"
                open={model.orderListModalVisible}
                onCancel={model.hideOrderListModal}
                footer={null}
                width={800}
                destroyOnClose
            >
                {model.selectedInvoice && (
                    <Table
                        columns={orderColumns}
                        dataSource={model.selectedInvoice.orderItems || []}
                        rowKey="orderId"
                        pagination={false}
                        size="small"
                    />
                )}
            </Modal>

            {/* 开票处理弹窗 */}
            <Modal
                title="开票处理"
                open={model.processModalVisible}
                onCancel={model.hideProcessModal}
                onOk={handleProcessInvoice}
                okText="确认开票"
                cancelText="取消"
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <label>发票号码：</label>
                    <Input
                        value={model.invoiceNo}
                        onChange={(e) => model.setInvoiceNo(e.target.value)}
                        placeholder="请输入发票号码"
                        style={{ marginTop: 8, borderRadius: 0 }}
                    />
                </div>
            </Modal>

            {/* 提示组件 */}
            <ShowHint
                message={hintMessage}
                visible={showHint}
                onClose={() => setShowHint(false)}
                duration={2000}
            />
        </div>
    );
});

export default InvoiceManagement; 